import type * as I from 'core/api/coreApiClient';
import ErrorPage from 'core/components/Screens/ErrorPage/ErrorPage';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import ExceptionLogger from 'core/utilities/ExceptionLogger/ExceptionLogger';
import I18n from 'i18n/en-US';
import type { ErrorInfo, GetDerivedStateFromError } from 'react';
import React from 'react';
import { connect } from 'react-redux';

interface PropsFromState {
  storeError: unknown;
}

interface ComponentProps extends PropsFromState {
  children: React.ReactNode;
}

interface ComponentState {
  error?: Error | string;
  errorInfo?: ErrorInfo;
  incident?: I.Incident;
}

function getDerivedStateFromError(error: Error, errorInfo: ErrorInfo) {
  return { error, errorInfo: errorInfo || { componentStack: '' } };
}

/**
 * Based on https://reactjs.org/docs/error-boundaries.html
 */
class ErrorBoundary extends React.Component<ComponentProps, ComponentState> {
  constructor(props: ComponentProps) {
    super(props);
    this.state = {};
  }

  componentDidUpdate(prevProps: ComponentProps) {
    const { error } = this.state;
    const { storeError } = this.props;

    // Use that variable to hide the react error overlay in development environment
    // to see the ErrorPage a user would see in production
    const showErrorPageInDevelopmentEnv = false;

    if (showErrorPageInDevelopmentEnv && process.env.NODE_ENV === 'development' && error) {
      setTimeout(() => {
        const iframe = document.querySelector('iframe');

        if (iframe) {
          iframe.style.display = 'none';
        }
      }, 1000);
    }

    if (storeError && !prevProps.storeError) {
      this.handleNewError(storeError as Error);
    }
  }

  static getDerivedStateFromError = getDerivedStateFromError as unknown as GetDerivedStateFromError<never, unknown>;

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.handleNewError(error, errorInfo);
  }

  async handleNewError(error: Error, errorInfo?: ErrorInfo) {
    const payload = await ExceptionLogger.log(error, errorInfo, []);

    this.setState({
      error,
      errorInfo,
      incident: payload,
    });
  }

  render() {
    const { error, errorInfo, incident } = this.state;
    const { children } = this.props;

    // The react error overlay is only generated in development environment and will hide the ErrorPage below.
    // Otherwise, the user will see the custom made ErrorPage presenting an incident ID along the error details.
    if (error) {
      let errorStack: string | undefined = errorInfo?.componentStack;

      try {
        if (
          !errorStack
          && typeof error !== 'string'
          && error.stack
        ) {
          errorStack = error.stack;
        }
      // eslint-disable-next-line no-empty
      } catch (e) {
      }

      return (
        <ErrorPage
          title={I18n.App.ErrorBoundary.title}
          e2eId="unhandledError"
          incident={incident}
          errorTitle={error.toString()}
          errorStack={process.env.NODE_ENV === 'development' ? errorStack : undefined}
          returnToDefaultFirstPage
        />
      );
    }

    return children;
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  storeError: state.error.error,
});

export default connect(
  mapStateToProps,
)(ErrorBoundary);
